import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/auth.store'
import MainLayout from './components/layout/MainLayout'
import MaintenanceNotice from './components/ui/MaintenanceNotice'
import { maintenanceApi } from './services/api'

// Landing Pages
import Home from './pages/landing/Home'
import Pricing from './pages/landing/Pricing'
import Contact from './pages/landing/Contact'
import Terms from './pages/landing/Terms'
import Privacy from './pages/landing/Privacy'
import LegalDocument from './pages/legal/LegalDocument'

// Auth Pages
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'

// Main Pages
import Dashboard from './pages/dashboard/Dashboard'
import BarterList from './pages/barters/BarterList'
import MyBarters from './pages/barters/MyBarters'
import BarterView from './pages/barters/BarterView'
import BarterCreate from './pages/barters/BarterCreate'
import BarterEdit from './pages/barters/BarterEdit'
import BarterGroupList from './pages/groups/BarterGroupList'
import BarterGroupDetail from './pages/groups/BarterGroupDetail'
import BarterRequests from './pages/requests/BarterRequests'
import Profile from './pages/profile/Profile'
import UserList from './pages/users/UserList'
import UserProfile from './pages/users/UserProfile'
import BarterCoins from './pages/coins/BarterCoins'
import Messages from './pages/messages/Messages'
import Reviews from './pages/reviews/Reviews'
import Support from './pages/support/Support'
import Settings from './pages/settings/Settings'
import EmailNotifications from './pages/notifications/EmailNotifications'
import Subscription from './pages/subscription/Subscription'
import Billing from './pages/billing/Billing'
import Withdrawals from './pages/withdrawals/Withdrawals'
import Analytics from './pages/analytics/Analytics'
import EscrowManagement from './pages/escrow/EscrowManagement'
import Statistics from './pages/admin/Statistics'
import PageNotifications from './pages/admin/PageNotifications'
import Coupons from './pages/admin/Coupons'
import DataPurge from './pages/admin/DataPurge'
import AuditLogs from './pages/admin/AuditLogs'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated ? <Navigate to="/dashboard" /> : <>{children}</>
}

function App() {
  const [maintenanceInfo, setMaintenanceInfo] = useState<{ message: string; end_datetime?: string | null } | null>(null)

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await maintenanceApi.get()
        if (response.data?.is_in_maintenance) {
          setMaintenanceInfo({
            message: response.data.maintenance?.maintenance_message || 'System is under maintenance',
            end_datetime: response.data.maintenance?.end_datetime || null
          })
        } else {
          setMaintenanceInfo(null)
        }
      } catch (error) {
        // Silently fail - don't show maintenance notice if we can't check
        console.error('Failed to check maintenance status:', error)
      }
    }

    checkMaintenance()
    // Check every 30 seconds
    const interval = setInterval(checkMaintenance, 30000)

    return () => clearInterval(interval)
  }, [])

  return (
    <BrowserRouter>
      {maintenanceInfo && (
        <MaintenanceNotice
          message={maintenanceInfo.message}
          endDateTime={maintenanceInfo.end_datetime}
        />
      )}
      <Routes>
        {/* Public Landing Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* Legal Documents */}
        <Route path="/legal/:documentType" element={<LegalDocument />} />

        {/* Auth Pages */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Subscription Page (Protected but outside dashboard) */}
        <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />

        {/* Protected Dashboard Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="my-barters" element={<MyBarters />} />
          <Route path="barters" element={<BarterList />} />
          <Route path="barters/create" element={<BarterCreate />} />
          <Route path="barters/:id" element={<BarterView />} />
          <Route path="barters/:id/edit" element={<BarterEdit />} />
          <Route path="groups" element={<BarterGroupList />} />
          <Route path="groups/:id" element={<BarterGroupDetail />} />
          <Route path="requests" element={<BarterRequests />} />
          <Route path="profile" element={<Profile />} />
          <Route path="users" element={<UserList />} />
          <Route path="users/:id" element={<UserProfile />} />
          <Route path="coins" element={<BarterCoins />} />
          <Route path="messages" element={<Messages />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="billing" element={<Billing />} />
          <Route path="support" element={<Support />} />
          <Route path="settings" element={<Settings />} />
          <Route path="notifications" element={<EmailNotifications />} />
          <Route path="subscription" element={<Subscription />} />
          <Route path="withdrawals" element={<Withdrawals />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="escrow" element={<EscrowManagement />} />
          <Route path="statistics" element={<Statistics />} />
          <Route path="coupons" element={<Coupons />} />
          <Route path="page-notifications" element={<PageNotifications />} />
          <Route path="data-purge" element={<DataPurge />} />
          <Route path="audit-logs" element={<AuditLogs />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export default App
